package floris.graphics.landing.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.columnGap
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.rowGap
import com.varabyte.kobweb.compose.ui.modifiers.scrollMargin
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.ChevronDownIcon
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import floris.graphics.core.constants.Res
import floris.graphics.core.navigation.Navigation
import floris.graphics.core.utils.pauseAllVideos
import floris.graphics.core.utils.reloadAllVideos
import com.materialdesignsystem.components.CheckeredListSection
import com.materialdesignsystem.components.svg.locationSvg
import com.materialdesignsystem.components.svg.mailSvg
import com.materialdesignsystem.components.svg.phoneSvg
import com.materialdesignsystem.components.widgets.DsExternalLink
import com.materialdesignsystem.constants.Attributes
import floris.graphics.core.Constants
import com.materialdesignsystem.constants.Identifiers
import com.materialdesignsystem.extensions.toHex
import com.materialdesignsystem.toColorScheme
import floris.graphics.landing.components.layouts.PageLayout
import floris.graphics.landing.components.widgets.ProjectItem
import floris.graphics.landing.components.widgets.TabList
import floris.graphics.models.domain.models.Techstack
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.plus
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Br
import kotlin.random.Random

@Page("/index")
@Composable
fun HomePage() {
    val ctx = rememberPageContext()
    val categoryId = remember(ctx.route) {
        ctx.route.params[Constants.CATEGORY_ID]
    }

    val viewModel = remember { HomeViewModel(categoryId) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(categoryId) {
        if (categoryId == state.queryCategoryId) return@LaunchedEffect

        pauseAllVideos()
        viewModel.sendIntent(HomeContract.Intent.SelectCategory(categoryId))
    }

    LaunchedEffect(state.projects) {
        reloadAllVideos()
    }

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest {
            when (it) {
                is HomeContract.Effect.Navigate -> ctx.router.navigateTo(it.route)
            }
        }
    }

    HomePageContent(state = state, sendIntent = viewModel::sendIntent)
}

@Composable
fun HomePageContent(
    state: HomeContract.State,
    sendIntent: (HomeContract.Intent) -> Unit
) {
    val breakpoint = rememberBreakpoint()
    val colorScheme = ColorMode.current.toColorScheme

    LaunchedEffect(breakpoint) {
        // Pause all videos when moving from mobile to non-mobile UI
        pauseAllVideos()
    }

    PageLayout(title = Res.String.Home) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(top = 40.px, bottom = 100.px)
                .rowGap(if (breakpoint >= Breakpoint.MD) 100.px else 50.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .rowGap(if (breakpoint >= Breakpoint.MD) 60.px else 30.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                CategorySelectionSection(
                    selectedItem = state.categories.find { it.id == state.queryCategoryId }?.name,
                    items = state.categories.map { it.name },
                    onSelectedItemChanged = { item ->
                        val categoryId = state.categories.find { it.name == item }?.id
                        sendIntent(HomeContract.Intent.Navigate(Navigation.Screen.Home.categorySelection(categoryId)))
                    }
                )

                CheckeredListSection(
                    modifier = Modifier
                        .maxWidth(Constants.SECTION_WIDTH.px)
                        .fillMaxWidth(if (breakpoint >= Breakpoint.MD) 80.percent else 90.percent),
                    items = state.projects,
                    calculateRandomGroupSize = { maxItemsInRow, leftovers, currentIndex ->
                        if (leftovers > maxItemsInRow) (1..maxItemsInRow).random(Random(currentIndex + 1)) else maxItemsInRow
                    }
                ) { project ->
                    ProjectItem(
                        project = project,
                        onClick = { pauseAllVideos() }
                    )
                }
            }

            TechstackSection(techstacks = state.techStacks)

            SimpleGrid(
                modifier = Modifier
                    .id("contact")
                    // This is needed so navigating to the fragment will take into account the header height
                    .scrollMargin(Constants.HEADER_HEIGHT.px + 40.px)
                    .fillMaxWidth(if (breakpoint >= Breakpoint.MD) 80.percent else 90.percent)
                    .maxWidth(Constants.SECTION_WIDTH.px),
                numColumns = numColumns(1, lg = 2)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .rowGap(48.px),
                    verticalArrangement = Arrangement.Center
                ) {
                    Column {
                        SpanText(Res.String.Greeting)
                        Br()
                        SpanText(Res.String.Greeting_2)
                        Br()
                        SpanText(Res.String.Greeting_3)
                    }

                    Column(
                        modifier = Modifier.rowGap(16.px)
                    ) {
                        val number = Res.String.ContactPhoneNumber
                        val mail = Res.String.ContactMail
                        val address = Res.String.ContactAddress
                        val location = Navigation.External.Maps(address.replace(" ", "+").replace(",", "")).route

                        DsExternalLink(
                            href = Navigation.External.Mail(mail).route,
                            text = mail,
                            icon = { mailSvg(colorScheme.primary) }
                        )

                        DsExternalLink(
                            href = location,
                            text = address,
                            icon = { locationSvg(colorScheme.primary) }
                        )

                        DsExternalLink(
                            href = Navigation.External.Phone(number).route,
                            text = number,
                            icon = { phoneSvg(colorScheme.primary) }
                        )
                    }
                }

                if (breakpoint >= Breakpoint.MD) {
                    Box(
                        modifier = Modifier.fillMaxWidth(),
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            modifier = Modifier.fillMaxWidth(85.percent),
                            src = "/floris-contact.svg"
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun CategorySelectionSection(
    selectedItem: String?,
    items: List<String>,
    onSelectedItemChanged: (String?) -> Unit
) {
    val colorScheme = ColorMode.current.toColorScheme
    val breakpoint = rememberBreakpoint()
    var showCollapse by remember { mutableStateOf(false) }

    if (breakpoint != Breakpoint.ZERO) {
        Box(
            Modifier
                .fillMaxWidth(90.percent)
                .display(DisplayStyle.Flex)
                .flexWrap(FlexWrap.Wrap)
                .columnGap(32.px)
                .rowGap(20.px)
                .justifyContent(JustifyContent.Center)
        ) {
            TabList(activeTab = selectedItem, items = items, onItemClicked = onSelectedItemChanged)
        }
    } else {
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .cursor(Cursor.Pointer)
                    .padding(topBottom = 12.px, leftRight = 5.percent)
                    .onClick {
                        showCollapse = !showCollapse
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fontWeight(FontWeight.Bold)
                        .fontSize(20.px)
                        .lineHeight(28.px),
                    text = Res.String.Categories
                )

                ChevronDownIcon(
                    modifier = Modifier
                        .size(28.px)
                        .attrsModifier {
                            attr(Attributes.StrokeWidth, "0")
                            attr(Attributes.Fill, colorScheme.onBackground.toHex())
                        }
                )
            }

            if (showCollapse) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .borderTop(2.px, LineStyle.Solid, colorScheme.outlineVariant)
                        .borderRadius(bottomRight = 8.px, bottomLeft = 8.px)
                        .boxShadow(0.px, 4.px, 4.px, 0.px, Colors.Black.copyf(alpha = 0.25f)),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        modifier = Modifier
                            .padding(topBottom = 32.px, leftRight = 16.px)
                            .rowGap(24.px),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        TabList(activeTab = selectedItem, items = items, onItemClicked = {
                            showCollapse = false
                            onSelectedItemChanged(it)
                        })
                    }
                }
            }
        }
    }
}

@Composable
private fun TechstackSection(
    techstacks: List<Techstack>
) {
    val colorScheme = ColorMode.current.toColorScheme
    val breakpoint = rememberBreakpoint()
    val techstackLogoSize = when {
        breakpoint >= Breakpoint.MD -> 100.px
        breakpoint == Breakpoint.SM -> 60.px
        else -> 40.px
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(colorScheme.secondaryContainer)
            .color(colorScheme.onSecondaryContainer)
            .padding(topBottom = if (breakpoint >= Breakpoint.MD) 80.px else 50.px),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .rowGap(if (breakpoint >= Breakpoint.MD) 60.px else 50.px)
                .maxWidth(Constants.SECTION_WIDTH.px)
                .fillMaxWidth(if (breakpoint >= Breakpoint.MD) 80.percent else 90.percent),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                modifier = Modifier
                    .fontWeight(FontWeight.Bold)
                    .fontSize(if (breakpoint >= Breakpoint.SM) 36.px else 22.px)
                    .textAlign(TextAlign.Center)
                    .lineHeight(if (breakpoint >= Breakpoint.SM) 46.px else 28.px),
                text = Res.String.TechnologiesMainlyUsed
            )

            Box(
                Modifier
                    .fillMaxWidth()
                    .display(DisplayStyle.Flex)
                    .flexWrap(FlexWrap.Wrap)
                    .rowGap(16.px)
                    .columnGap(16.px)
                    .justifyContent(JustifyContent.Center)
            ) {
                techstacks
                    .flatMap { listOf(it) + it.subTechstacks.orEmpty() }
                    .forEach {
                        Image(
                            modifier = Modifier.size(techstackLogoSize),
                            src = it.logoMediaFile?.url?.toString() ?: "",
                            alt = "Logo, ${it.name}"
                        )
                    }
            }
        }
    }
}
